import { IRoute } from '../../sp-types';
import { IBuyingInstruction } from '@sp-types/buyingInstructionsType';
import { IMaterialExposure } from '@sp-types/netExposureType';
import { MonthYearValue } from '@utils/MonthYear/MonthYearValue';
import {
  getDateFromKey,
  getDateKey,
  getInstrument,
  getInventoryCalendar,
  getPrevMonth,
  IPositionRanges,
  makeDateMap,
  isRouteAvailable,
  sortByDate,
  toDate,
} from './balanceUtils';

export const generatePositionInstructions = (exposure: IMaterialExposure, routes: IRoute[]): IBuyingInstruction[] => {
  const exposureCalendars = calcExposureCalendar(exposure);

  const positionMap = exposureToPositionRangesMap(exposureCalendars);
  const positions = getDesiredPositions(positionMap);

  // check here which of the routes is availble in these dates

  const buyInstructions = convertToRoutes(routes, positions);

  return buyInstructions;
};

export const calcExposureCalendar = ({
  futureExpCalendar,
  positionExpCalendar,
  inventoryCalendar,
}: IMaterialExposure): MonthYearValue[] => {
  const inventoryExpCalendar = getInventoryCalendar(inventoryCalendar?.value || 0);
  const exposureMap = makeDateMap([
    ...inventoryExpCalendar,
    ...positionExpCalendar,
    ...futureExpCalendar.map(({ date, value }) => ({ date, value: -value })),
  ]);

  return Object.entries(exposureMap).map(([monthStr, value]) => ({ date: getDateFromKey(monthStr), value }));
};

const exposureToPositionRangesMap = (monthExposures: MonthYearValue[]): IPositionRanges =>
  monthExposures.sort(sortByDate).reduce((result, { date, value }) => {
    if (!result.current) {
      result.firstValue = value;
      result.current = value;
      result.lastMonth = date.month;
      result.lastYear = date.year;
      return result;
    }

    const isPositive = result.firstValue > 0;

    if (result.completed === 1) {
      return result;
    }
    if (value === 0 || (isPositive && value < 0) || (!isPositive && value > 0)) {
      // that means we passed the exposure for this time
      result.completed = 1;
      return result;
    }

    if ((isPositive && result.current > value) || (!isPositive && result.current < value)) {
      result[getDateKey(getPrevMonth(date))] = result.current - value;
      result.current = value;
    }
    result.lastMonth = date.month;
    result.lastYear = date.year;
    return result;
  }, {} as IPositionRanges);

const getDesiredPositions = (positionsMap: IPositionRanges): MonthYearValue[] => [
  ...Object.entries(positionsMap)
    .filter(([key, _value]) => !['lastMonth', 'lastYear', 'current', 'firstValue', 'completed'].includes(key))
    .map(([key, value]) => ({ value, date: getDateFromKey(key) })),
  { value: positionsMap.current, date: { year: positionsMap.lastYear, month: positionsMap.lastMonth } },
];

const convertToRoutes = (routes: IRoute[], positionRanges: MonthYearValue[]): IBuyingInstruction[] => {
  let noRouteAvailble = false;
  const buyInstructions = positionRanges.reduce((result, mon) => {
    //TODO: support case when need to split range to several routes
    //const routeQuantity-
    const availableRoute = routes.find((route) => isRouteAvailable(route, mon));

    noRouteAvailble = noRouteAvailble || !availableRoute;
    if (noRouteAvailble) {
      //return [];
      console.log('add Notification that there is exposue unprotected');
    }

    const instructions = availableRoute?.instruments?.map(
      ({ id, amountPerRoute, quantityPerContract }) =>
        ({
          action: mon.value < 0 ? 'buy' : 'sell',
          positionType: mon.value < 0 ? 'long' : 'short',
          expiryDate: toDate(mon.date),
          instrumentId: getInstrument(id, mon.date),
          routeId: availableRoute?.routeId || availableRoute?.id || '',
          routeQuantity: Math.floor(Math.abs(mon.value * amountPerRoute)),
          numberOfContracts: 0,
          formulaId: availableRoute.formulaId,
          multiplier: amountPerRoute,
          routePercent: quantityPerContract,
          //expectedPrice:availableRoute.futures?.
        } as IBuyingInstruction),
    );
    return [...result, ...(instructions || [])];
  }, [] as IBuyingInstruction[]);

  return groupByInstrumentKey(buyInstructions); //noRouteAvailble ? [] :
};

const groupByInstrumentKey = (buyInstructions: IBuyingInstruction[]) => {
  const groupedByInstrument = buyInstructions.reduce((acc, instruction) => {
    if (acc[instruction.instrumentId]) {
      instruction.routeQuantity = instruction.routeQuantity + acc[instruction.instrumentId].routeQuantity;
    }
    return { ...acc, [instruction.instrumentId]: instruction };
  }, {} as Record<string, IBuyingInstruction>);
  return Object.values(groupedByInstrument);
};
